import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Receipt,
  PlusCircle,
  Download,
  Calendar,
  Package,
  Building2,
  Megaphone,
  Zap,
  Scissors,
  Shield,
  GraduationCap,
  Laptop,
  Car,
  FileText,
  X,
  Loader2,
  PieChart,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  CreditCard,
  Banknote,
  Crown
} from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const EXPENSE_ICONS = {
  supplies: Package,
  rent: Building2,
  marketing: Megaphone,
  utilities: Zap,
  equipment: Scissors,
  insurance: Shield,
  education: GraduationCap,
  software: Laptop,
  travel: Car,
  other: Receipt
};

const AccountingDashboard = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [period, setPeriod] = useState("month");
  
  // Data states
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [profitLoss, setProfitLoss] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [income, setIncome] = useState([]);
  const [taxSummary, setTaxSummary] = useState(null);
  const [categories, setCategories] = useState({ expense_categories: [], income_categories: [] });
  
  // Modal states
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showAddIncome, setShowAddIncome] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [expenseForm, setExpenseForm] = useState({
    category: "",
    amount: "",
    description: "",
    vendor: "",
    date: new Date().toISOString().split('T')[0]
  });
  
  const [incomeForm, setIncomeForm] = useState({
    category: "",
    amount: "",
    description: "",
    client_name: "",
    payment_method: "card",
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    checkAccess();
  }, [token]);

  useEffect(() => {
    if (hasAccess) {
      fetchAllData();
    }
  }, [hasAccess, period]);

  const checkAccess = async () => {
    try {
      const res = await axios.get(`${API}/accounting/check-access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasAccess(res.data.has_access);
      
      if (res.data.has_access) {
        const catRes = await axios.get(`${API}/accounting/categories`);
        setCategories(catRes.data);
      }
    } catch (error) {
      console.error("Failed to check access:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllData = async () => {
    try {
      const [summaryRes, plRes, expRes, incRes] = await Promise.all([
        axios.get(`${API}/accounting/dashboard-summary`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/accounting/profit-loss?period=${period}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/accounting/expenses`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/accounting/income`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setDashboardSummary(summaryRes.data);
      setProfitLoss(plRes.data);
      setExpenses(expRes.data.expenses || []);
      setIncome(incRes.data.income || []);
    } catch (error) {
      console.error("Failed to fetch accounting data:", error);
    }
  };

  const fetchTaxSummary = async () => {
    try {
      const res = await axios.get(`${API}/accounting/tax-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaxSummary(res.data);
    } catch (error) {
      console.error("Failed to fetch tax summary:", error);
    }
  };

  const handleAddExpense = async () => {
    if (!expenseForm.category || !expenseForm.amount || !expenseForm.description) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/accounting/expenses`, {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Expense added successfully");
      setShowAddExpense(false);
      setExpenseForm({
        category: "",
        amount: "",
        description: "",
        vendor: "",
        date: new Date().toISOString().split('T')[0]
      });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to add expense");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddIncome = async () => {
    if (!incomeForm.category || !incomeForm.amount || !incomeForm.description) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/accounting/income`, {
        ...incomeForm,
        amount: parseFloat(incomeForm.amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Income recorded successfully");
      setShowAddIncome(false);
      setIncomeForm({
        category: "",
        amount: "",
        description: "",
        client_name: "",
        payment_method: "card",
        date: new Date().toISOString().split('T')[0]
      });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to record income");
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = async () => {
    const today = new Date();
    const startDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
    const endDate = today.toISOString().split('T')[0];
    
    try {
      const res = await axios.get(
        `${API}/accounting/export?start_date=${startDate}&end_date=${endDate}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `myhairchair_accounting_${startDate}_to_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Report exported successfully");
    } catch (error) {
      toast.error("Failed to export report");
    }
  };

  const deleteExpense = async (expenseId) => {
    try {
      await axios.delete(`${API}/accounting/expenses/${expenseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Expense deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete expense");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <Card className="border-amber-500/30 bg-gradient-to-br from-amber-50 to-orange-50">
        <CardContent className="py-12 text-center">
          <Crown className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-800 mb-2">Elite Feature</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Accounting & financial tracking is available exclusively for Elite members. 
            Upgrade to unlock profit/loss reports, expense tracking, and tax summaries.
          </p>
          <Button className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold">
            Upgrade to Elite - $49/month
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Wallet className="w-6 h-6 text-amber-500" />
            Accounting
          </h2>
          <p className="text-gray-500 text-sm">Track income, expenses, and profitability</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button 
            size="sm" 
            className="bg-red-500 hover:bg-red-600 text-white"
            onClick={() => setShowAddExpense(true)}
          >
            <PlusCircle className="w-4 h-4 mr-2" />
            Add Expense
          </Button>
          <Button 
            size="sm" 
            className="bg-green-500 hover:bg-green-600 text-white"
            onClick={() => setShowAddIncome(true)}
          >
            <PlusCircle className="w-4 h-4 mr-2" />
            Add Income
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b pb-2">
        {[
          { id: "overview", label: "Overview", icon: PieChart },
          { id: "expenses", label: "Expenses", icon: TrendingDown },
          { id: "income", label: "Income", icon: TrendingUp },
          { id: "tax", label: "Tax Summary", icon: FileText }
        ].map(tab => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? "default" : "ghost"}
            size="sm"
            onClick={() => {
              setActiveTab(tab.id);
              if (tab.id === "tax" && !taxSummary) fetchTaxSummary();
            }}
            className={activeTab === tab.id ? "bg-amber-500 hover:bg-amber-600" : ""}
          >
            <tab.icon className="w-4 h-4 mr-2" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Period Selector */}
      {activeTab === "overview" && (
        <div className="flex items-center gap-2">
          <Label className="text-sm text-gray-600">Period:</Label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Overview Tab */}
      {activeTab === "overview" && profitLoss && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 font-medium">Total Income</p>
                    <p className="text-2xl font-bold text-green-700">
                      ${profitLoss.total_income.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-green-200 rounded-full flex items-center justify-center">
                    <ArrowUpRight className="w-5 h-5 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600 font-medium">Total Expenses</p>
                    <p className="text-2xl font-bold text-red-700">
                      ${profitLoss.total_expenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-red-200 rounded-full flex items-center justify-center">
                    <ArrowDownRight className="w-5 h-5 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={`${profitLoss.net_profit >= 0 ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-600 font-medium">Net Profit</p>
                    <p className={`text-2xl font-bold ${profitLoss.net_profit >= 0 ? 'text-amber-700' : 'text-red-700'}`}>
                      ${profitLoss.net_profit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-amber-200 rounded-full flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-purple-200 bg-purple-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-medium">Profit Margin</p>
                    <p className="text-2xl font-bold text-purple-700">
                      {profitLoss.profit_margin}%
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-purple-200 rounded-full flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Breakdown Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Income Breakdown */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  Income Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                {profitLoss.income_breakdown.length > 0 ? (
                  <div className="space-y-3">
                    {profitLoss.income_breakdown.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <DollarSign className="w-4 h-4 text-green-600" />
                          </div>
                          <span className="text-sm font-medium">{item.name}</span>
                        </div>
                        <span className="font-semibold text-green-600">
                          ${item.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm text-center py-4">No income recorded yet</p>
                )}
              </CardContent>
            </Card>

            {/* Expense Breakdown */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-red-500" />
                  Expense Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                {profitLoss.expense_breakdown.length > 0 ? (
                  <div className="space-y-3">
                    {profitLoss.expense_breakdown.map((item, idx) => {
                      const IconComponent = EXPENSE_ICONS[item.category] || Receipt;
                      return (
                        <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                              <IconComponent className="w-4 h-4 text-red-600" />
                            </div>
                            <span className="text-sm font-medium">{item.name}</span>
                          </div>
                          <span className="font-semibold text-red-600">
                            ${item.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm text-center py-4">No expenses recorded yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Expenses Tab */}
      {activeTab === "expenses" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>All Expenses</span>
              <Badge variant="outline" className="text-red-600">
                Total: ${expenses.reduce((sum, e) => sum + e.amount, 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {expenses.length > 0 ? (
              <div className="space-y-3">
                {expenses.map((expense) => {
                  const IconComponent = EXPENSE_ICONS[expense.category] || Receipt;
                  return (
                    <div key={expense.expense_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                          <IconComponent className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{expense.description}</p>
                          <p className="text-sm text-gray-500">
                            {expense.vendor && `${expense.vendor} • `}
                            {expense.date}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-red-600">
                          -${expense.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteExpense(expense.expense_id)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No expenses recorded yet</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setShowAddExpense(true)}
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Add Your First Expense
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Income Tab */}
      {activeTab === "income" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>All Income</span>
              <Badge variant="outline" className="text-green-600">
                Total: ${income.reduce((sum, i) => sum + i.amount, 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {income.length > 0 ? (
              <div className="space-y-3">
                {income.map((inc) => (
                  <div key={inc.income_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{inc.description}</p>
                        <p className="text-sm text-gray-500">
                          {inc.client_name && `${inc.client_name} • `}
                          {inc.date}
                          <Badge variant="outline" className="ml-2 text-xs">
                            {inc.payment_method === "card" && <CreditCard className="w-3 h-3 mr-1" />}
                            {inc.payment_method === "cash" && <Banknote className="w-3 h-3 mr-1" />}
                            {inc.payment_method}
                          </Badge>
                        </p>
                      </div>
                    </div>
                    <span className="font-semibold text-green-600">
                      +${inc.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No income recorded yet</p>
                <p className="text-sm text-gray-400 mt-1">Completed appointments are automatically tracked</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tax Summary Tab */}
      {activeTab === "tax" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-500" />
              Tax Summary {taxSummary?.year}
            </CardTitle>
            <CardDescription>
              Annual summary for tax preparation
            </CardDescription>
          </CardHeader>
          <CardContent>
            {taxSummary ? (
              <div className="space-y-6">
                {/* Annual Totals */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600">Total Income</p>
                    <p className="text-xl font-bold text-green-700">
                      ${taxSummary.total_income.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-600">Total Expenses</p>
                    <p className="text-xl font-bold text-red-700">
                      ${taxSummary.total_expenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <p className="text-sm text-amber-600">Net Profit</p>
                    <p className="text-xl font-bold text-amber-700">
                      ${taxSummary.net_profit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>

                {/* Quarterly Breakdown */}
                <div>
                  <h4 className="font-semibold mb-3">Quarterly Breakdown</h4>
                  <div className="grid grid-cols-4 gap-3">
                    {taxSummary.quarterly_breakdown.map((q) => (
                      <div key={q.quarter} className="p-3 border rounded-lg">
                        <p className="font-medium text-gray-800">{q.quarter}</p>
                        <p className="text-sm text-green-600">+${q.income.toFixed(2)}</p>
                        <p className="text-sm text-red-600">-${q.expenses.toFixed(2)}</p>
                        <p className={`text-sm font-medium ${q.profit >= 0 ? 'text-amber-600' : 'text-red-600'}`}>
                          Net: ${q.profit.toFixed(2)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Deductible Expenses */}
                <div>
                  <h4 className="font-semibold mb-3">Deductible Expenses by Category</h4>
                  <div className="space-y-2">
                    {taxSummary.deductible_expenses.map((exp, idx) => (
                      <div key={idx} className="flex justify-between py-2 border-b">
                        <span className="text-gray-700">{exp.name}</span>
                        <span className="font-medium">${exp.amount.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Estimated Tax */}
                <div className="p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-600">Estimated Self-Employment Tax (15.3%)</p>
                  <p className="text-xl font-bold text-purple-700">
                    ${taxSummary.estimated_self_employment_tax.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-purple-500 mt-1">{taxSummary.note}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add Expense Modal */}
      {showAddExpense && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-red-500" />
                Add Expense
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Category *</Label>
                <Select 
                  value={expenseForm.category} 
                  onValueChange={(v) => setExpenseForm({...expenseForm, category: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.expense_categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                    className="pl-8"
                  />
                </div>
              </div>
              <div>
                <Label>Description *</Label>
                <Input
                  placeholder="What was this expense for?"
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                />
              </div>
              <div>
                <Label>Vendor (Optional)</Label>
                <Input
                  placeholder="Where did you purchase?"
                  value={expenseForm.vendor}
                  onChange={(e) => setExpenseForm({...expenseForm, vendor: e.target.value})}
                />
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={expenseForm.date}
                  onChange={(e) => setExpenseForm({...expenseForm, date: e.target.value})}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowAddExpense(false)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-red-500 hover:bg-red-600"
                  onClick={handleAddExpense}
                  disabled={submitting}
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Expense"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Income Modal */}
      {showAddIncome && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-500" />
                Record Income
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Category *</Label>
                <Select 
                  value={incomeForm.category} 
                  onValueChange={(v) => setIncomeForm({...incomeForm, category: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.income_categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={incomeForm.amount}
                    onChange={(e) => setIncomeForm({...incomeForm, amount: e.target.value})}
                    className="pl-8"
                  />
                </div>
              </div>
              <div>
                <Label>Description *</Label>
                <Input
                  placeholder="Service or product description"
                  value={incomeForm.description}
                  onChange={(e) => setIncomeForm({...incomeForm, description: e.target.value})}
                />
              </div>
              <div>
                <Label>Client Name (Optional)</Label>
                <Input
                  placeholder="Client name"
                  value={incomeForm.client_name}
                  onChange={(e) => setIncomeForm({...incomeForm, client_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select 
                  value={incomeForm.payment_method} 
                  onValueChange={(v) => setIncomeForm({...incomeForm, payment_method: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={incomeForm.date}
                  onChange={(e) => setIncomeForm({...incomeForm, date: e.target.value})}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowAddIncome(false)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-green-500 hover:bg-green-600"
                  onClick={handleAddIncome}
                  disabled={submitting}
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Record Income"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AccountingDashboard;
